import {
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@cmps/ng-bootstrap';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';

import { TranslationKeyMap } from '../../coaching-insight.i18n';
import {
    CoachingDateToday,
    ColumnConfigItem,
    PersonOfInterest,
} from '../models/coaching.global.model';
import { FLOAMetric, FLOAPerson } from '../models/floa.model';
import { CoachingInsightWidgetService } from '../services/coaching-insight.widget.service';
import { FrontlineOAService } from '../services/frontline-oa.service';
import { PersonOfInterestService } from '../services/person-of-interest.service';

// maintain a list of Team Member Metrics
@Component({
    selector: '[frontlinePersonRow]',
    templateUrl: './frontline-person-row.component.html',
    host: {
        class: 'team-floa-row',
    },
})
export class FrontlinePersonRowComponent implements OnInit {
    @Input() selectedUser: PersonOfInterest;
    @Input() active: string[];
    @Input() floaPerson: FLOAPerson;
    @Input() periodTypeId: number;
    @Input() periodTypeDate: Date;

    // present when current person row is a subordinate and will roll up
    @Input() maxUncoached: number;
    @Input() maxCoached: number;

    @Output() metricsRendered: EventEmitter<boolean> =
        new EventEmitter<boolean>();

    @Output() showingExpandMenu: EventEmitter<boolean> =
        new EventEmitter<boolean>();

    @Output() selectedUserChange: EventEmitter<PersonOfInterest> =
        new EventEmitter<PersonOfInterest>();

    @Output() collapsed: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() receivedSubordinates: EventEmitter<FLOAPerson[]> =
        new EventEmitter<FLOAPerson[]>();

    @Output() receivedMetrics: EventEmitter<FLOAMetric[]> = new EventEmitter<
        FLOAMetric[]
    >();

    @ViewChild('fractionHoverTpl', { static: true })
    fractionHoverTpl: TemplateRef<any>;

    @HostBinding('class.menu-shown')
    public get isMenuShown(): boolean {
        return this.floaPerson.isExpandedMetrics === true;
    }

    @HostBinding('class.row-expanded')
    public get isRowExpanded(): boolean {
        return this.floaPerson.isExpanded === true;
    }

    public columns: Observable<ColumnConfigItem[]>;

    i18n = TranslationKeyMap;
    hasPendingData = false;
    loadingFloaTeam = false;
    loadingFloaMetrics = false;

    public activetab = 'tab-performance';
    public activecolumns: Observable<number>;
    public _modal: NgbModalRef;

    barcolours = {
        numeratorColour: 'green',
        denominatorColour: 'red',
    };

    constructor(
        private readonly _floaService: FrontlineOAService,
        private readonly _coachingInsightService: CoachingInsightWidgetService,
        private readonly _modalService: NgbModal,
        private readonly _personOfInterestService: PersonOfInterestService,
        public activeModal: NgbActiveModal,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        this.columns = this._floaService.oacolumns;
        this.columns.pipe(first()).subscribe();

        if (
            (this.maxCoached === null && this.maxUncoached === null) ||
            this.floaPerson.isSubordinate === false
        ) {
            return;
        }

        if (this.floaPerson.isTop) {
            this.loadTeamData();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    hideMenu() {
        this.floaPerson.isExpandedMetrics = false;
        this.showingExpandMenu.emit(this.floaPerson.isExpandedMetrics);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getFloaPersonMetrics() {
        // if already expanded, collapse
        if (this.floaPerson.isExpandedMetrics) {
            this.receivedMetrics.emit(null);
            this.collapsed.emit(true);
            this.loadingFloaMetrics = false;
            return;
        }

        this.loadingFloaMetrics = true;
        const p = this._floaService.retrieveFLOAMetrics(
            this.floaPerson.userId,
            this.periodTypeId || this._coachingInsightService.getPeriodTypeId(),
            this.periodTypeDate || CoachingDateToday,
            false,
        );

        if (p) {
            const f = p.subscribe(
                (metrics) => {
                    f.unsubscribe();

                    this.receivedMetrics.emit(metrics || []);

                    this.loadingFloaMetrics = false;
                },
                () => {
                    this.loadingFloaMetrics = false;
                },
            );
        }
    }

    loadTeamData(): void {
        this.loadingFloaTeam = true;

        const p = this._floaService.retrieveTeamFLOA(
            this.floaPerson.userId,
            this.periodTypeId || this._coachingInsightService.getPeriodTypeId(),
            this.periodTypeDate || new Date(), // Substitute 'new Date()' with 'CoachingDateToday' if applicable
        );

        if (p) {
            const f = p.subscribe(
                (team) => {
                    f.unsubscribe();
                    this.receivedSubordinates.emit(team || []);
                    this.loadingFloaTeam = false;
                    // eslint-disable-next-line n/handle-callback-err
                },
                // eslint-disable-next-line n/handle-callback-err
                (error) => {
                    this.loadingFloaTeam = false;
                },
            );
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getFloaPersonDR() {
        // if already expanded, collapse
        if (this.floaPerson.isExpanded) {
            this.receivedSubordinates.emit(null);
            this.collapsed.emit(true);
            this.loadingFloaTeam = false;
            return;
        }

        if (this.floaPerson.bottomRole === true) {
            this.openOA();
            return;
        }

        this.loadTeamData();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    openOA() {
        const user = {
            lastName: this.floaPerson.lastName,
            firstName: this.floaPerson.firstName,
            value: this.floaPerson.userId.toString(),
            label: this.floaPerson.fullName,
            userId: this.floaPerson.userId,
        };
        this._personOfInterestService.swapAndStoreFavourite(user);
        this._floaService.saveCurrentViewState();
        this.selectedUserChange.emit(user);
    }
}
